import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const HERO_A = "https://images.pexels.com/photos/1066171/pexels-photo-1066171.jpeg";
const HERO_B = "https://images.unsplash.com/photo-1762843354010-c07e2b16ec92";
const ITEM_A = "https://images.pexels.com/photos/9558265/pexels-photo-9558265.jpeg";
const ITEM_B = "https://images.pexels.com/photos/65676/nanjing-studio-jeans-65676.jpeg";

const Marquee = () => (
  <div className="marquee py-10 border-y border-black/10 relative">
    <div className="marquee-track font-serif-display text-6xl md:text-8xl text-black/80">
      {["a", "b"].map((k) => (
        <span key={k} className="flex gap-32">
          <span>Undress the algorithm</span><span className="text-[color:var(--accent)]">·</span>
          <span>Dress the mirror</span><span className="text-[color:var(--accent)]">·</span>
          <span>Any store. Any silhouette.</span><span className="text-[color:var(--accent)]">·</span>
        </span>
      ))}
    </div>
  </div>
);

const Step = ({ n, title, body }) => (
  <div className="flex gap-6 py-8 border-t border-black/10">
    <div className="font-serif-display text-5xl leading-none text-[color:var(--accent)]">{n}</div>
    <div>
      <div className="font-mono-eyebrow mb-2">Chapter {n}</div>
      <h3 className="font-serif-display text-3xl leading-tight mb-3">{title}</h3>
      <p className="text-black/60 max-w-md leading-relaxed">{body}</p>
    </div>
  </div>
);

const Landing = () => {
  return (
    <div className="relative">
      {/* HERO — asymmetric editorial split */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-16 pb-24">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="font-mono-eyebrow mb-8">Issue N° 001 · Winter Edition</div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: "easeOut" }}
              className="font-serif-display text-6xl md:text-[8rem] leading-[0.9] tracking-tight"
              data-testid="hero-title"
            >
              Try it on.<br/>
              <span className="italic text-[color:var(--accent)]">Before</span> it ships.
            </motion.h1>
            <p className="mt-10 max-w-xl text-lg text-black/70 leading-relaxed">
              Paste a link from any boutique or big-box store, upload your portrait, and watch
              the garment settle onto <em>your</em> shoulders. A private dressing room powered
              by generative imaging — no returns, no regrets.
            </p>
            <div className="flex gap-4 mt-12">
              <Link to="/auth?mode=signup" className="btn-ink" data-testid="hero-cta">Enter the atelier</Link>
              <a href="#howitworks" className="btn-outline-ink" data-testid="hero-explore">Read the process</a>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5">
            <div className="relative">
              <div className="aspect-[3/4] overflow-hidden bg-neutral-200 polaroid">
                <img src={HERO_A} alt="editorial" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-10 -left-10 w-40 md:w-52 aspect-[3/4] overflow-hidden bg-white polaroid border border-black/5 hidden md:block">
                <img src={HERO_B} alt="secondary" className="w-full h-full object-cover" />
              </div>
              <div className="mt-6 flex items-center gap-3">
                <span className="tick" />
                <span className="font-mono-eyebrow">Photographed by generative light</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* HOW IT WORKS */}
      <section id="howitworks" className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5 md:sticky md:top-32 self-start">
            <div className="font-mono-eyebrow mb-6">The process</div>
            <h2 className="font-serif-display text-5xl md:text-7xl leading-none">A dressing room<br/><em>without walls.</em></h2>
            <p className="mt-8 text-black/70 leading-relaxed max-w-md">
              Three chapters between the desire and the delivery. Every step designed
              to make certainty feel effortless.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7">
            <Step n="01" title="Enter your portrait" body="Upload a clear photograph — daylight, plain background. This becomes the constant, the north star of every fitting." />
            <Step n="02" title="Import from anywhere" body="Paste the link of a coat, a dress, a pair of loafers. We lift the product image quietly, no accounts, no signals." />
            <Step n="03" title="Watch it become you" body="Nano Banana composes a photorealistic image with your likeness, correct drape, real shadow. Save the ones that feel like home." />
            <div className="mt-10 border-t border-black/10 pt-8 flex items-center justify-between">
              <div className="font-mono-eyebrow">Signed silhouettes are archived in your wardrobe</div>
              <Link to="/auth?mode=signup" className="editorial-link font-mono-eyebrow" data-testid="how-cta">Begin — free →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Item strip */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono-eyebrow mb-4">Wardrobe · February</div>
            <h3 className="font-serif-display text-4xl leading-tight">A moodboard of<br/>what might be.</h3>
          </div>
          <div className="col-span-12 md:col-span-8 grid grid-cols-3 gap-4">
            {[ITEM_A, ITEM_B, HERO_B].map((src, i) => (
              <motion.div key={src}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="aspect-[3/4] overflow-hidden bg-white border border-black/5 polaroid">
                <img src={src} alt="" className="w-full h-full object-cover"/>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/10 mt-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-6">
            <div className="font-serif-display text-4xl">Atelier<span className="text-[color:var(--accent)]">.</span></div>
            <p className="mt-4 text-black/60 max-w-sm">A private mirror for a public wardrobe. Built for the curious, the picky, and the well-dressed to be.</p>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="font-mono-eyebrow mb-4">Objects</div>
            <ul className="space-y-2 text-black/70">
              <li>Virtual Try-On</li><li>Outfit Composer</li><li>Cross-Store Wardrobe</li>
            </ul>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="font-mono-eyebrow mb-4">House</div>
            <ul className="space-y-2 text-black/70">
              <li>Manifesto</li>
              <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-[color:var(--accent)]">Privacy</Link></li>
              <li><Link to="/terms" data-testid="footer-terms" className="hover:text-[color:var(--accent)]">Terms</Link></li>
              <li>Contact</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-black/10">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-6 flex items-center justify-between font-mono-eyebrow">
            <span>© 2026 Atelier</span>
            <span>Made with silk and silicon</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
